import { useMemo } from 'react';
import { msg } from '@gonfalon/messages/msg';
import { useParentSize } from '@visx/responsive';

import { useAutoMargin } from '../../../utils/useAutoMargin';
import type { LineChartProps } from '../../types/LineChartProps';
import { extractXAxisConfig } from '../../utils/extractXAxisConfig';
import { extractYAxisConfig } from '../../utils/extractYAxisConfig';
import { estimateYAxisNumTicks } from '../../utils/tickCalculation';
import { ChartCanvas } from '../ChartProvider/ChartCanvas';
import { ChartProvider, useChartProviderState } from '../ChartProvider/ChartProvider';

import styles from './LineChart.module.css';

const IGNORE_PARENT_SIZE_DIMS: Array<'width' | 'top' | 'left'> = ['width', 'top', 'left'];

/**
 * Composable LineChart component built on @visx/xychart.
 *
 * A thin wrapper around XYChart that provides LaunchPad theming and a lightweight
 * context for child components to coordinate.
 *
 * @example
 * ```tsx
 * <LineChart height={400}>
 *   <XAxis dataKey="timestamp" dataType="time" />
 *   <YAxis dataType="time" />
 *   <Grid />
 *   <Series
 *     seriesKey="latency"
 *     name="API Latency"
 *     data={data}
 *     dataKey="latency"
 *     showMarkers
 *   />
 *   <Tooltip enabled />
 * </LineChart>
 * ```
 */
export function LineChart({
  data,
  referenceLineData,
  children,
  width,
  height,
  margin,
  autoMargin = true,
  ariaLabel = msg('dataviz.chart.lineChartLabel'),
  disableDragToZoom = false,
  isLoading = false,
  onZoomChange,
  onDataPointHover,
  emptyStateTitle,
  emptyStateSubtitle,
  xDataKey: xDataKeyProp,
  xDataType: xDataTypeProp,
  ...rest
}: LineChartProps) {
  const { parentRef, height: parentHeight } = useParentSize({
    initialSize: { height: typeof height === 'number' ? height : 0 },
    ignoreDimensions: IGNORE_PARENT_SIZE_DIMS,
  });

  const defaultNumTicks = useMemo(
    () => (parentHeight > 0 ? estimateYAxisNumTicks(parentHeight) : undefined),
    [parentHeight],
  );

  const yAxisConfig = useMemo(
    () => extractYAxisConfig(children, referenceLineData, defaultNumTicks),
    [children, referenceLineData, defaultNumTicks],
  );

  // Extract XAxis configuration from children BEFORE creating provider.
  // Parent-provided xDataKey/xDataType win over <XAxis> child props, so callers that
  // hide the axis (no <XAxis> child) can still wire x correctly.
  const extracted = useMemo(() => extractXAxisConfig(children, 'line'), [children]);
  const xDataKey = xDataKeyProp ?? extracted.xDataKey;
  const dataType = xDataTypeProp ?? extracted.dataType;
  const { xScaleType, hasXAxis, xAxisLabelOffset } = extracted;

  // Provider computes yScaleConfig from visible data (auto-scales during zoom)
  const chartProviderValue = useChartProviderState({
    chartType: 'line',
    data,
    referenceLineData,
    disableDragToZoom,
    isLoading,
    xDataKey,
    xScaleType,
    xDataType: dataType,
    onZoomChange,
    yAxisConfig,
    onDataPointHover,
    emptyStateTitle,
    emptyStateSubtitle,
  });

  const yScaleDomain = chartProviderValue.yScaleConfig?.domain;

  const chartMargin = useAutoMargin({
    hasXAxis,
    xAxisLabelOffset,
    hasYAxis: yAxisConfig.hasYAxis,
    yTickValues: chartProviderValue.yTickValues,
    yScaleDomain,
    yAxisDataType: yAxisConfig.yAxisDataType,
    yAxisTickFormat: yAxisConfig.yAxisTickFormat,
    hasYAxisRight: yAxisConfig.right?.hasYAxis ?? false,
    yTickValuesRight: chartProviderValue.yTickValuesRight,
    yScaleRightDomain: chartProviderValue.yScaleRightConfig?.domain,
    yAxisDataTypeRight: yAxisConfig.right?.yAxisDataType,
    yAxisTickFormatRight: yAxisConfig.right?.yAxisTickFormat,
    yAxisLabelOffsetRight: yAxisConfig.right?.yAxisLabelOffset ?? 0,
    enabled: autoMargin,
    manualMargin: margin,
  });

  return (
    <div
      ref={parentRef}
      className={styles.container}
      style={{ width: width ?? '100%', height: height ?? '100%' }}
      role="img"
      aria-label={ariaLabel}
    >
      <ChartProvider value={chartProviderValue}>
        <ChartCanvas
          height={typeof height === 'number' ? height : undefined}
          width={typeof width === 'number' ? width : undefined}
          margin={chartMargin}
          {...rest}
        >
          {children}
        </ChartCanvas>
      </ChartProvider>
    </div>
  );
}
